import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ListChecks, Sliders } from 'lucide-react';
import { AttributeType, type AttributeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useAttributesList, useAttributeMutations } from '~/hooks/use-attributes';
import { useAttributeCategoriesList } from '~/hooks/use-attribute-categories';
import { tokenToForm, type AttributeFormValues } from '~/lib/schemas/attribute';
import { AttributeSheet } from './attribute-sheet';
import { AttributeValuesSheet } from './attribute-values-sheet';

/** Attributes (technical characteristics) CRUD surface — parity with the legacy admin grid. */
export function AttributesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useAttributesList();
	const { data: categories = [] } = useAttributeCategoriesList();
	const { remove } = useAttributeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<AttributeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<AttributeToken | null>(null);
	const [valuesFor, setValuesFor] = useState<{ id: number; nm: string } | null>(null);

	const categoryName = useMemo(() => {
		const map = new Map<number, string>();
		for (const c of categories) if (c.id != null) map.set(c.id, c.nm ?? '');
		return map;
	}, [categories]);

	const columns = useMemo<SystemGridColumn<AttributeToken>[]>(
		() => [
			{
				key: 'name',
				header: t('attributes.list.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: a => a.nm,
				exportValue: a => a.nm,
				render: a => <span className="truncate text-sm font-medium text-foreground">{a.nm}</span>
			},
			{
				key: 'code',
				header: t('attributes.list.columns.code'),
				width: '140px',
				sortValue: a => a.cd ?? '',
				exportValue: a => a.cd ?? '',
				render: a => <span className="font-mono text-xs text-foreground-muted">{a.cd}</span>
			},
			{
				key: 'erpCode',
				header: t('attributes.list.erpCode'),
				width: '140px',
				sortValue: a => a.erpcd ?? '',
				exportValue: a => a.erpcd ?? '',
				render: a => <span className="font-mono text-xs text-foreground-muted">{a.erpcd}</span>
			},
			{
				key: 'category',
				header: t('attributes.list.columns.category'),
				width: '160px',
				sortValue: a => (a.acid != null ? (categoryName.get(a.acid) ?? '') : ''),
				exportValue: a => (a.acid != null ? (categoryName.get(a.acid) ?? '') : ''),
				render: a => <span className="truncate text-sm text-foreground-muted">{a.acid != null ? categoryName.get(a.acid) : '—'}</span>
			},
			{
				key: 'type',
				header: t('attributes.list.columns.type'),
				width: '130px',
				sortValue: a => a.at ?? 0,
				exportValue: a => (a.at != null ? t(`attributes.types.${a.at}`) : ''),
				render: a => (a.at != null ? <Badge variant="outline">{t(`attributes.types.${a.at}`)}</Badge> : null)
			},
			{
				key: 'valueDataType',
				header: t('attributes.list.columns.valueDataType'),
				width: '130px',
				sortValue: a => a.vt ?? 0,
				exportValue: a => (a.vt != null ? t(`attributes.valueDataTypes.${a.vt}`) : ''),
				render: a => <span className="text-sm text-foreground-muted">{a.vt != null ? t(`attributes.valueDataTypes.${a.vt}`) : ''}</span>
			},
			{
				key: 'visible',
				header: t('attributes.list.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: a => (a.iv ? 1 : 0),
				exportValue: a => (a.iv ? t('common.yes') : t('common.no')),
				render: a => (a.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'disableFromFilters',
				header: t('attributes.list.disableFromFilters'),
				width: '110px',
				align: 'center',
				defaultVisible: false,
				sortValue: a => (a.daf ? 1 : 0),
				exportValue: a => (a.daf ? t('common.yes') : t('common.no')),
				render: a => (a.daf ? <Check size={16} className="text-foreground-muted" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'image',
				header: t('attributes.list.image'),
				width: '80px',
				align: 'center',
				defaultVisible: false,
				exportValue: a => a.img1 ?? '',
				render: a =>
					a.img1 ? (
						<img src={a.img1} alt="" className="size-8 rounded border border-border object-contain" />
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'sort',
				header: t('attributes.list.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: a => a.si ?? 0,
				exportValue: a => a.si ?? 0,
				render: a => <span className="text-sm tabular-nums text-foreground-muted">{a.si}</span>
			}
		],
		[t, categoryName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<AttributeToken>
				gridId="attributes"
				title={t('attributes.list.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={a => a.id ?? a.nm}
				columns={columns}
				searchPredicate={(a, q) => `${a.nm ?? ''} ${a.cd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Sliders size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('attributes.list.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={a => {
					setEditing(tokenToForm(a));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={a =>
					a.at === AttributeType.ListOfValues && a.id != null ? (
						<Button
							variant="ghost"
							size="icon"
							aria-label={t('attributes.values.title')}
							title={t('attributes.values.title')}
							onClick={() => setValuesFor({ id: a.id!, nm: a.nm ?? '' })}>
							<ListChecks size={15} />
						</Button>
					) : null
				}
				exportFilename="attributes"
			/>

			<AttributeSheet open={editOpen} onOpenChange={setEditOpen} initial={editing} />
			<AttributeValuesSheet open={Boolean(valuesFor)} onOpenChange={o => !o && setValuesFor(null)} attribute={valuesFor} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={o => !o && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.list.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.list.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
